import { FC, KeyboardEvent, useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DoNotDisturbAltRoundedIcon from "@mui/icons-material/DoNotDisturbAltRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import SaveIcon from "@mui/icons-material/Save";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import {
	AlertColor,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	List,
	ListItem,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import { Translate } from "@/components/Application/Translate";

import { ProcedureUpdateNotice } from "@/model/Competence/ProcedureUpdateNotice";

import { useFetchProcedureKnowledgeStatus } from "@/service/ProcedureQueries";

interface ProcedureKnowledgeModalProps {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
}

type ChipColor = "default" | "success" | "warning" | "info";

const STATUS_META: Record<
	number,
	{
		chipLabel: string;
		chipTranslation: string;
		chipColor: ChipColor;
		alertColor: AlertColor;
		titleKey: string;
		titleFallback: string;
		title: string; // fallback text if translations unavailable
		Icon: typeof InfoOutlinedIcon;
	}
> = {
	0: {
		chipLabel: "Not Required",
		chipTranslation: "ProcedureKnowledge.Modal.ChipNotRequired",
		chipColor: "default",
		alertColor: "info",
		titleKey: "ProcedureKnowledge.Modal.HeaderNotRequired",
		titleFallback:
			"Your current role does not require knowledge of this procedure",
		title: "Not required",
		Icon: DoNotDisturbAltRoundedIcon,
	},
	1: {
		chipLabel: "Not Started",
		chipTranslation: "ProcedureKnowledge.Modal.ChipNotStarted",
		chipColor: "warning",
		alertColor: "warning",
		titleKey: "ProcedureKnowledge.Modal.HeaderNotStarted",
		titleFallback: "Please confirm your knowledge of this procedure",
		title: "Not started",
		Icon: PriorityHighRoundedIcon,
	},
	2: {
		chipLabel: "Not Complete",
		chipTranslation: "ProcedureKnowledge.Modal.ChipNotComplete",
		chipColor: "info",
		alertColor: "info",
		titleKey: "ProcedureKnowledge.Modal.NotComplete",
		titleFallback: "You are not finished with this procedure",
		title: "Not complete",
		Icon: InfoOutlinedIcon,
	},
	3: {
		chipLabel: "Review",
		chipTranslation: "ProcedureKnowledge.Modal.ChipReview",
		chipColor: "info",
		alertColor: "info",
		titleKey: "ProcedureKnowledge.Modal.HeaderReview",
		titleFallback:
			"The procedure has changed, please confirm your knowledge of the updated procedure",
		title: "Review changes",
		Icon: InfoOutlinedIcon,
	},
	4: {
		chipLabel: "Completed",
		chipTranslation: "ProcedureKnowledge.Modal.ChipCompleted",
		chipColor: "success",
		alertColor: "success",
		titleKey: "ProcedureKnowledge.Modal.HeaderCompleted",
		titleFallback:
			"Great! You have already confirmed knowledge on this procedure",
		title: "Completed",
		Icon: TaskAltRoundedIcon,
	},
};

export const ProcedureKnowledgeModal: FC<ProcedureKnowledgeModalProps> = ({
	open,
	onClose,
	onConfirm,
}) => {
	const theme = useTheme();
	const { data: knowledge } = useFetchProcedureKnowledgeStatus();

	const status = knowledge?.Status as number | undefined;
	const meta = status !== undefined ? STATUS_META[status] : undefined;
	const isKnowledgeNeeded = status === 1 || status === 3;
	const updates = (status === 3 && knowledge?.Updates) || [];

	const headerColor = useMemo(() => {
		if (!meta) return theme.palette.text.primary;
		const palette = {
			success: theme.palette.success.main,
			warning: theme.palette.warning.main,
			info: theme.palette.info.main,
			error: theme.palette.error.main,
		} as const;
		return palette[meta.alertColor] ?? theme.palette.text.primary;
	}, [meta, theme]);

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (isKnowledgeNeeded && (e.key === "Enter" || e.key === "NumpadEnter")) {
			// Allow quick confirm via Enter
			e.preventDefault();
			onConfirm();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			onKeyDown={handleKeyDown}
			PaperProps={{
				sx: {
					borderRadius: 3,
					overflow: "hidden",
				},
			}}
		>
			{/* Title row with contextual icon + color accent */}
			<DialogTitle
				sx={{
					pr: 6,
					pb: 1,
					display: "flex",
					alignItems: "center",
					gap: 1.25,
				}}
			>
				{meta && (
					<meta.Icon
						fontSize="small"
						sx={{
							color: headerColor,
							flexShrink: 0,
						}}
					/>
				)}
				<Typography variant="h6" sx={{ fontWeight: 800 }}>
					{meta ? (
						<Translate String={meta.titleKey}>{meta.titleFallback}</Translate>
					) : (
						<Translate>Procedure knowledge</Translate>
					)}
				</Typography>

				{/* Close button */}
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{ position: "absolute", right: 8, top: 8 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					pt: 1.5,
					"& .MuiDialogContent-dividers": {
						borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
						borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
					},
				}}
			>
				{/* Status row */}
				<Box
					sx={{
						p: 1.25,
						borderRadius: 2,
						border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
						backgroundColor:
							theme.palette.mode === "light"
								? alpha(theme.palette.primary.main, 0.04)
								: alpha(theme.palette.primary.main, 0.08),
						display: "flex",
						alignItems: "center",
						gap: 1,
						mb: 2,
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 700 }}>
						<Translate String="ProcedureKnowledge.Modal.Status">
							Status:
						</Translate>
					</Typography>

					{meta ? (
						<Chip
							color={meta.chipColor}
							label={
								<Translate String={meta.chipTranslation}>
									{meta.chipLabel}
								</Translate>
							}
							size="small"
							sx={{ fontWeight: 700 }}
						/>
					) : (
						<Chip size="small" label={<Translate>Error</Translate>} />
					)}
				</Box>

				{/* Updates section (only for Review state) */}
				{status === 3 && Array.isArray(updates) && updates.length > 0 && (
					<Box
						sx={{
							border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
							borderRadius: 2,
							overflow: "hidden",
						}}
					>
						<Box
							sx={{
								px: 2,
								py: 1,
								borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
								backgroundColor:
									theme.palette.mode === "light"
										? alpha(theme.palette.background.paper, 0.6)
										: alpha(theme.palette.background.default, 0.4),
							}}
						>
							<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
								<Translate String="ProcedureKnowledge.Modal.Updates">
									Recent updates
								</Translate>{" "}
								<Typography
									component="span"
									variant="caption"
									color="text.secondary"
								>
									({updates.length})
								</Typography>
							</Typography>
						</Box>

						<List
							dense
							sx={{
								py: 0.5,
								// subtle timeline-style accent
								"& .MuiListItem-root": {
									alignItems: "flex-start",
									columnGap: 1,
								},
							}}
						>
							{updates.map((u: ProcedureUpdateNotice, idx: number) => (
								<ListItem key={idx} sx={{ px: 2 }}>
									<Box
										sx={{
											mt: 0.75,
											mr: 1,
											width: 6,
											height: 6,
											borderRadius: "50%",
											backgroundColor: theme.palette.info.main,
											flexShrink: 0,
										}}
									/>
									<Typography variant="body2">{u.Description}</Typography>
								</ListItem>
							))}
						</List>
					</Box>
				)}

				{/* Fallback error (missing status) */}
				{status === undefined && (
					<>
						<Divider sx={{ my: 2 }} />
						<Typography color="error" variant="body2">
							<Translate String="ProcedureKnowledge.Modal.Error">
								Error
							</Translate>
						</Typography>
					</>
				)}
			</DialogContent>

			{/* Sticky footer actions */}
			<DialogActions
				sx={{
					position: "sticky",
					bottom: 0,
					zIndex: 1,
					bgcolor:
						theme.palette.mode === "light"
							? alpha(theme.palette.background.paper, 0.9)
							: alpha(theme.palette.background.default, 0.9),
					backdropFilter: "blur(6px)",
					borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
					py: 1.25,
					px: 2,
					gap: 1,
				}}
			>
				{isKnowledgeNeeded && (
					<Button
						variant="contained"
						color="success"
						startIcon={<SaveIcon />}
						onClick={onConfirm}
						sx={{
							height: 40,
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 800,
						}}
					>
						<Translate String="ProcedureKnowledge.Modal.Confirm">
							Confirm
						</Translate>
					</Button>
				)}

				<Box sx={{ flex: 1 }} />

				<Button
					variant="outlined"
					color="warning"
					startIcon={<CloseIcon />}
					onClick={onClose}
					sx={{
						height: 40,
						borderRadius: 2,
						textTransform: "none",
						fontWeight: 800,
					}}
				>
					<Translate String="ProcedureKnowledge.Modal.Close">Close</Translate>
				</Button>
			</DialogActions>
		</Dialog>
	);
};
